import React from 'react';
import { LucideIcon } from 'lucide-react';

interface ButtonProps {
  onClick?: () => void;
  type?: 'button' | 'submit';
  children: React.ReactNode;
  className?: string; // Additional className
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'disabled'; // Button variants
  disabled?: boolean;
  isLoading?: boolean;
  icon?: LucideIcon;
  iconPosition?: 'left' | 'right';
}

const Button: React.FC<ButtonProps> = ({
  onClick,
  type = 'button',
  children,
  className,
  variant = 'primary',
  disabled = false,
  isLoading = false,
  icon: Icon,
  iconPosition = 'left',
}) => {
  const baseClasses = 'inline-flex items-center justify-center px-6 py-2 transition-colors text-sm font-medium rounded text-nowrap';

  // Define classes for each button variant with dark mode styles
  const variantClasses = {
    primary:
      'bg-primary text-white hover:bg-oxblood dark:bg-primary dark:text-white dark:hover:bg-oxblood cursor-pointer',
    secondary:
      'border border-secondary text-primary bg-white hover:bg-secondary hover:text-white dark:border-seam dark:bg-charcoal dark:text-cream dark:hover:bg-seam dark:hover:text-white cursor-pointer',
    success:
      'bg-green-500 text-white hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-500 cursor-pointer',
    danger:
      'bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-500 cursor-pointer',
    disabled:
      'bg-gray-400 text-gray-600 cursor-not-allowed hover:bg-gray-400 dark:bg-seam dark:text-harbor',
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center">
          {children}
          <span className="ml-2 spinner-border animate-spin inline-block w-4 h-4 border-2 rounded-full border-t-white" />
        </div>
      );
    }

    return (
      <>
        {Icon && iconPosition === 'left' && <Icon className="w-4 h-4 mr-2" />}
        {children}
        {Icon && iconPosition === 'right' && <Icon className="w-4 h-4 ml-2" />}
      </>
    );
  };

  // Apply base classes, variant classes, and additional className
  return (
    <button
      type={type}
      onClick={onClick}
      className={`${baseClasses} ${variantClasses[variant]} ${className || ''}`}
      disabled={disabled}
    >
      {renderContent()}
    </button>
  );
};

export default Button;
